import {
  queryOptions,
  useMutation,
  type UseMutationResult,
} from "@tanstack/react-query"
import { errorFormatter, request } from "@utils/request"

export type WebhookEvent = "LiveSummaryCreated"
export type WebhookMethod = "POST" | "PUT" | "PATCH"
export type WebhookPayloadType = "Full" | "Uid"

export type Webhook = {
  uid: string
  solutionUid: string
  createdByUserUid: string
  created: Date
  updatedByUserUid: string
  updated: Date
  title: string | null
  description: string | null
  event: WebhookEvent
  method: WebhookMethod
  url: string
  headers: Record<string, string> | null
  payloadType: WebhookPayloadType
  enabled: boolean
}

// Omit the server owned fields. What is left is everything the client sends.
export type NewWebhook = Omit<
  Webhook,
  | "uid"
  | "solutionUid"
  | "createdByUserUid"
  | "created"
  | "updatedByUserUid"
  | "updated"
>

const webhooksQueryKey = ["webhooks"] as const

export const webhooksQuery = () =>
  queryOptions({
    queryKey: [webhooksQueryKey],
    // The endpoint wraps the list, so unwrap here and let consumers read
    // data flat. Simply to avoid having to write webhooks.webhooks when rendering.
    queryFn: async () => {
      const { webhooks } = await request.get<{ webhooks: Webhook[] }>(
        `/api/webhooks/v1`,
      )
      return webhooks
    },
  })

export const useFetchUnredactedWebhook = (): UseMutationResult<
  Webhook,
  Error,
  Webhook
> => {
  return useMutation({
    //Remove from cache after the modal closes, so we dont store the real url and headers in cache.
    gcTime: 0,
    mutationFn: ({ solutionUid, uid }: Webhook) =>
      request.get<Webhook>(`/api/webhooks/v1/${solutionUid}/${uid}/unredacted`),
  })
}

// PATCH sends any subset of the client-owned fields, plus the uid in the path.
export type WebhookUpdate = Partial<NewWebhook> & { uid: string }

export const useCreateWebhook = (): UseMutationResult<
  Webhook,
  Error,
  NewWebhook
> => {
  return useMutation({
    mutationFn: (webhook: NewWebhook) =>
      request.post<Webhook>("/api/webhooks/v1", { json: webhook }),
    onSuccess: async (_data, _variables, _result, { client }) => {
      await client.invalidateQueries({ queryKey: webhooksQueryKey })
    },
  })
}

export const useUpdateWebhook = (): UseMutationResult<
  Webhook,
  Error,
  WebhookUpdate
> => {
  return useMutation({
    mutationFn: ({ uid, ...webhook }: WebhookUpdate) =>
      request.patch<Webhook>(`/api/webhooks/v1/${uid}`, { json: webhook }),
    onSuccess: async (_data, _variables, _result, { client }) => {
      await client.invalidateQueries({ queryKey: webhooksQueryKey })
    },
  })
}

export const useDeleteWebhook = (): UseMutationResult<
  unknown,
  Error,
  string
> => {
  return useMutation({
    mutationFn: (uid: string) =>
      errorFormatter(request.request.delete(`/api/webhooks/v1/${uid}`)),
    onSuccess: async (_data, _variables, _result, { client }) => {
      await client.invalidateQueries({ queryKey: webhooksQueryKey })
    },
  })
}
